"use client";
import { motion, useAnimation, AnimatePresence } from "framer-motion";
import { useState, useRef, useEffect } from "react";
import {
  FiMapPin,
  FiPhone,
  FiMail,
  FiUser,
  FiMessageSquare,
} from "react-icons/fi";
import { FaPaperPlane } from "react-icons/fa";

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  const controls = useAnimation();
  const formRef = useRef<HTMLFormElement>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    await controls.start({
      y: 10,
      transition: { duration: 0.2 },
    });
    await controls.start({
      y: 0,
      transition: { duration: 0.3, type: "spring" },
    });

    const formDataToSend = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      formDataToSend.append(key, value);
    });

    formDataToSend.append("_subject", "New Form Submission");

    try {
      const res = await fetch("https://formspree.io/f/xjkozkal", {
        method: "POST",
        body: formDataToSend,
        headers: {
          Accept: "application/json",
        },
      });

      if (res.ok) {
        setIsSubmitted(true);
        setShowThankYou(true);
        setFormData({ name: "", email: "", phone: "", message: "" });

        setTimeout(() => {
          setShowThankYou(false);
          setIsSubmitted(false);
        }, 5000);
      } else {
        alert("Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error(err);
      alert("Submission failed.");
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      controls.start({
        y: [0, -5, 0],
        transition: { duration: 3, repeat: Infinity, repeatType: "reverse" },
      });
    }, 3000);
    return () => clearInterval(interval);
  }, [controls]);

  return (
    <section id="contact-us" className="contact">
      <div className="contact__container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="contact__header"
        >
          <h2 className="contact__title">
            Let&apos;s <span className="contact__title-accent">Connect</span>
          </h2>
          <p className="contact__subtitle">
            Have a project in mind? Reach out and let&apos;s create something
            amazing together.
          </p>
        </motion.div>

        <div className="contact__content">
          <AnimatePresence mode="wait">
            {!showThankYou ? (
              <motion.form
                key="form"
                ref={formRef}
                onSubmit={handleSubmit}
                className="contact__form"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <motion.div
                  className="contact__form-decor contact__form-decor--1"
                  animate={controls}
                />
                <motion.div
                  className="contact__form-decor contact__form-decor--2"
                  animate={controls}
                />

                {/* Name */}
                <div className="contact__form-group">
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    viewport={{ once: true }}
                  >
                    <label htmlFor="name" className="contact__label">
                      <FiUser className="contact__label-icon" />
                      Your Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="contact__input"
                      required
                    />
                  </motion.div>
                </div>

                {/* Email */}
                <div className="contact__form-group">
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    viewport={{ once: true }}
                  >
                    <label htmlFor="email" className="contact__label">
                      <FiMail className="contact__label-icon" />
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="contact__input"
                      required
                    />
                  </motion.div>
                </div>

                {/* Phone */}
                <div className="contact__form-group">
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.45 }}
                    viewport={{ once: true }}
                  >
                    <label htmlFor="phone" className="contact__label">
                      <FiPhone className="contact__label-icon" />
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="contact__input"
                      pattern="[\d\s()+-]{7,}"
                      required
                    />
                  </motion.div>
                </div>

                {/* Message */}
                <div className="contact__form-group">
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                    viewport={{ once: true }}
                  >
                    <label htmlFor="message" className="contact__label">
                      <FiMessageSquare className="contact__label-icon" />
                      Your Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      className="contact__textarea"
                      rows={5}
                      required
                    />
                  </motion.div>
                </div>

                {/* Submit */}
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                  viewport={{ once: true }}
                >
                  <motion.button
                    animate={controls}
                    whileHover={{
                      scale: 1.02,
                      boxShadow: "0 5px 15px rgba(59, 130, 246, 0.3)",
                    }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    className="contact__submit"
                    disabled={isSubmitted}
                  >
                    {isSubmitted ? (
                      "Message Sent!"
                    ) : (
                      <>
                        Send Message{" "}
                        <FaPaperPlane className="contact__submit-icon" />
                      </>
                    )}
                  </motion.button>
                </motion.div>
              </motion.form>
            ) : (
              <motion.div
                key="thankyou"
                className="contact__thankyou"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
              >
                <h3>Thanks for contacting us!</h3>
                <p>We&apos; ll get back to you shortly.</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="contact__info"
          >
            <motion.div className="contact__info-decor" animate={controls} />

            <h3 className="contact__info-title">Other Ways to Reach Us</h3>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ staggerChildren: 0.1, delayChildren: 0.3 }}
              viewport={{ once: true }}
              className="contact__info-items"
            >
              <motion.div className="contact__info-item" whileHover={{ x: 5 }}>
                <div className="contact__info-icon-wrapper">
                  <FiMapPin className="contact__info-icon" />
                </div>
                <div>
                  <h4 className="contact__info-item-title">Location</h4>
                  <p className="contact__info-text">Canterbury, NSW</p>
                </div>
              </motion.div>

              <motion.div className="contact__info-item" whileHover={{ x: 5 }}>
                <div className="contact__info-icon-wrapper">
                  <FiPhone className="contact__info-icon" />
                </div>
                <div>
                  <h4 className="contact__info-item-title">Phone</h4>
                  <p className="contact__info-text">+61 493 370 722</p>
                </div>
              </motion.div>

              <motion.div className="contact__info-item" whileHover={{ x: 5 }}>
                <div className="contact__info-icon-wrapper">
                  <FiMail className="contact__info-icon" />
                </div>
                <div>
                  <h4 className="contact__info-item-title">Email</h4>
                  <p className="contact__info-text">info@perftecht.com.au</p>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
